import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MessageCircle,
  Phone,
  Sparkles,
  Check,
  ArrowRight,
  Globe,
} from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingCTA from "@/components/site/FloatingCTA";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CONTACT } from "@/data/content";

const heroImg =
  "https://images.unsplash.com/photo-1571086291540-b137111fa1c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMGFpcnBsYW5lJTIwbG9hZGluZ3xlbnwwfHx8fDE3ODM4OTYwMDN8MA&ixlib=rb-4.1.0&q=85";

const DESTINATIONS = [
  {
    code: "IN",
    flag: "🇮🇳",
    name: "India",
    ports: "DEL · BOM · BLR · MAA · HYD · CCU",
    detail:
      "K-beauty into India — CDSCO cosmetic-import licence + BIS compliance handled on our licences (you do not need your own). Door-delivery to 19,000+ Indian pincodes. DDP at ₹1,500/kg all-inclusive (customs duty, IGST, CDSCO handling, and door delivery included).",
  },
  {
    code: "GB",
    flag: "🇬🇧",
    name: "United Kingdom",
    ports: "LHR · STN · MAN",
    detail:
      "K-beauty into UK — Cosmetic Product Notification Portal (CPNP-UK / SCPN) responsible-person compliance handled on our end. Door-delivery UK-wide via LHR clearance desk.",
  },
  {
    code: "AE",
    flag: "🇦🇪",
    name: "UAE (Dubai)",
    ports: "DXB · AUH · SHJ",
    detail:
      "K-beauty into UAE — Emirates Authority for Standardization & Metrology (ESMA) product registration + Dubai Municipality cosmetic notification handled by our partner desk. Door-delivery Dubai and Abu Dhabi.",
  },
  {
    code: "HK",
    flag: "🇭🇰",
    name: "Hong Kong",
    ports: "HKG",
    detail:
      "K-beauty into Hong Kong — no product-registration barrier for most cosmetics (HK is a free-trade port for personal-care goods). Fastest lane on this list — often 48–72 hours door-to-door.",
  },
  {
    code: "US",
    flag: "🇺🇸",
    name: "United States",
    ports: "JFK · LAX · ORD · SFO",
    detail:
      "K-beauty into USA — FDA cosmetic facility registration (MoCRA) and product-listing compliance handled on our partner-desk registrations. Door-delivery to all 50 states.",
  },
  {
    code: "CA",
    flag: "🇨🇦",
    name: "Canada",
    ports: "YYZ · YVR · YUL",
    detail:
      "K-beauty into Canada — Health Canada Cosmetic Notification Form (CNF) filings and CBSA duty clearance handled on our partner registrations. Door-delivery all provinces.",
  },
];

const PRODUCTS = [
  "Sheet masks & essence-soaked treatments",
  "Serums (vitamin C, niacinamide, snail mucin)",
  "Sunscreens & SPF products",
  "Cleansing balms, oils & foams",
  "Cushion foundations & BB creams",
  "Lip tints, lip masks & lip oils",
  "Toners, ampoules & essences",
  "Retinol & peptide creams",
  "Eye creams & under-eye patches",
  "Body care, hand creams & scrubs",
  "Hair-care (scalp treatments, tonics)",
  "Fragrances & perfumes",
];

const FAQS = [
  {
    q: "Can I import Korean cosmetics without my own licence or documentation?",
    a: "Yes. Custom Clearance Partner imports Korean cosmetics against our own licence portfolio — CDSCO (India), CPNP-UK/SCPN Responsible Person (UK), ESMA + Dubai Municipality (UAE), Health Canada CNF (Canada), FDA MoCRA (USA) — and delivers duty-paid, door-to-door to your address as a private consignee. You do not need to acquire any cosmetics-import licence yourself. Perfect for individuals, dropshippers, resellers, salon owners, and first-time K-beauty importers.",
  },
  {
    q: "What Korean cosmetics can you source and ship?",
    a: "Any legally saleable K-beauty product — sheet masks, essences and serums, sunscreens, cushion foundations, lip tints, cleansing balms and oils, retinol and peptide creams, eye patches, body care, hair care, and fragrances. Brands we regularly source: COSRX, Innisfree, Laneige, Sulwhasoo, Beauty of Joseon, Anua, Numbuzin, Torriden, Some By Mi, Mediheal, Etude House, Missha, TIRTIR, Skin1004, and dozens more from Seoul and Busan supplier networks.",
  },
  {
    q: "How much does it cost to import Korean cosmetics duty-paid?",
    a: "Our all-inclusive DDP rate covers Korean-side sourcing, air transport, destination customs duty and taxes, and door delivery. India (with our CDSCO licensed clearance): ₹1,500/kg all-inclusive. For UK, UAE, Hong Kong, USA, and Canada — WhatsApp +91 78003 20078 with the destination and expected monthly volume for a firm quote. Volume slabs discount by 15–30% for regular buyers.",
  },
  {
    q: "Do you handle Korean cosmetics wholesale sourcing?",
    a: "Yes. We source directly from Korean brand distributors and licensed wholesalers in Seoul, Busan, and Incheon. Wholesale MOQs typically start at USD 500–2,000 depending on the brand. We consolidate multi-brand orders, warehouse briefly at our Incheon partner desk, and dispatch as a single DDP consignment to your destination.",
  },
  {
    q: "How fast is delivery from Korea to India / UK / UAE / USA / Canada / Hong Kong?",
    a: "Hong Kong: 48–72 hours door-to-door (fastest — free-trade port). India: 5 days end-to-end. UAE: 4–6 days. UK: 5–7 days. USA: 6–8 days. Canada: 7–9 days. All timelines are door-to-door and include destination customs clearance on our side.",
  },
  {
    q: "Do you help dropshippers import Korean cosmetics?",
    a: "Yes. We operate the full 3PL stack for K-beauty dropshippers selling to Indian, UK, US, UAE, Canadian, and Hong Kong end-customers on Shopify, Amazon, Nykaa (India), Sephora marketplaces, and their own storefronts. Bulk consolidation, destination-country compliance, warehousing, GST/VAT-invoiced last-mile to end-customer, and returns handling.",
  },
  {
    q: "Are the products authentic? Do you source from Korean brand distributors directly?",
    a: "Yes. We source only from Korean-brand-authorised distributors and licensed wholesalers in Seoul, Busan, and Incheon. Every consignment ships with brand invoice, batch/lot codes, and manufacturing dates. No parallel imports, no counterfeits.",
  },
  {
    q: "Can I get a sample or trial shipment before committing to volume?",
    a: "Yes. We support trial shipments starting at 5 kg for first-time buyers so you can test the workflow, delivery time, and end-customer experience before scaling. WhatsApp +91 78003 20078 to arrange.",
  },
];

export default function KoreanCosmeticsSourcing() {
  useEffect(() => {
    document.title =
      "Korean Cosmetics Sourcing & Duty-Paid Door Delivery to India, UK, UAE, USA, Canada, Hong Kong | K-Beauty Import | Custom Clearance Partner";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content =
      "Source Korean cosmetics from authorised Seoul & Busan distributors and get duty-paid door delivery to India, UK, UAE (Dubai), Hong Kong, USA, and Canada. No import licence, no CDSCO / CPNP / FDA / ESMA / Health Canada registration needed from you — we import against our own licences and deliver to your door. K-beauty wholesale, dropshipping, and personal orders. WhatsApp +91 78003 20078.";

    let kw = document.querySelector('meta[name="keywords"][data-kbeauty]');
    if (!kw) {
      kw = document.createElement("meta");
      kw.name = "keywords";
      kw.setAttribute("data-kbeauty", "1");
      document.head.appendChild(kw);
    }
    kw.content =
      "Korean cosmetics sourcing, K-beauty sourcing agent, import Korean cosmetics without licence, buy Korean cosmetics wholesale India, K-beauty wholesale UK, Korean cosmetics UAE Dubai, K-beauty USA import, Korean cosmetics Canada import, K-beauty Hong Kong, duty paid door delivery K-beauty, DDP Korean cosmetics, K-beauty dropshipping, COSRX Innisfree Laneige wholesale, Korean skincare wholesale India, K-beauty sourcing agent Seoul, buy Korean cosmetics for reselling";

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = "https://customclearancepartner.in/korean-cosmetics-sourcing";

    const id = "ccp-kbeauty-jsonld";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://customclearancepartner.in/korean-cosmetics-sourcing",
          name: "Korean Cosmetics Sourcing & Duty-Paid Door Delivery to India, UK, UAE, USA, Canada, Hong Kong",
          inLanguage: "en",
          about: { "@id": "https://customclearancepartner.in/#org" },
          speakable: {
            "@type": "SpeakableSpecification",
            xpath: ["//*[@id='kbeauty-h1']", "//*[@id='kbeauty-tldr']"],
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://customclearancepartner.in/" },
            { "@type": "ListItem", position: 2, name: "Korean Cosmetics Sourcing", item: "https://customclearancepartner.in/korean-cosmetics-sourcing" },
          ],
        },
        {
          "@type": "Service",
          name: "Korean Cosmetics Sourcing & Duty-Paid DDP Door Delivery",
          alternateName: [
            "K-beauty sourcing agent",
            "Import Korean cosmetics without licence",
            "Buy Korean cosmetics wholesale India",
            "K-beauty wholesale UK",
            "Korean cosmetics Dubai UAE",
            "K-beauty USA import",
            "Korean cosmetics Canada",
            "K-beauty Hong Kong",
            "DDP Korean cosmetics door delivery",
            "K-beauty dropshipping sourcing",
          ],
          serviceType: "Sourcing and Duty-Paid Cross-Border Delivery",
          areaServed: [
            { "@type": "Country", name: "India" },
            { "@type": "Country", name: "United Kingdom" },
            { "@type": "Country", name: "United Arab Emirates" },
            { "@type": "Country", name: "Hong Kong" },
            { "@type": "Country", name: "United States" },
            { "@type": "Country", name: "Canada" },
          ],
          provider: { "@id": "https://customclearancepartner.in/#org" },
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            price: "1500",
            unitText: "per kg all-inclusive DDP to India (CDSCO clearance included; UK/UAE/HK/US/CA quoted on WhatsApp)",
            availability: "https://schema.org/InStock",
          },
          description:
            "Custom Clearance Partner sources authentic K-beauty from authorised Korean distributors and delivers duty-paid to India, UK, UAE (Dubai), Hong Kong, USA, and Canada — no import licence needed from the buyer. Perfect for wholesalers, dropshippers, resellers, and individual K-beauty enthusiasts.",
        },
        {
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(script);
    return () => document.getElementById(id)?.remove();
  }, []);

  return (
    <div className="grain min-h-screen bg-[#F9FAFB] text-[#101828]" data-testid="kbeauty-page">
      <Header />

      <section className="border-b border-[#E4E7EC] bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-5 py-16 md:px-8 md:py-24">
          <nav className="font-mono-accent flex items-center gap-3 text-[#475467]" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#D92D20]">HOME</Link>
            <span>/</span>
            <span className="text-[#101828]">KOREAN COSMETICS SOURCING</span>
          </nav>

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="overline mb-4 flex items-center gap-3">
                <Sparkles className="h-4 w-4 text-[#D92D20]" strokeWidth={2.5} />
                🇰🇷 K-Beauty · Seoul → World · DDP
              </p>
              <h1
                id="kbeauty-h1"
                data-testid="kbeauty-h1"
                className="font-display text-4xl font-black leading-[0.98] tracking-tight text-[#101828] sm:text-5xl md:text-6xl lg:text-7xl"
              >
                Korean cosmetics, <span className="text-[#D92D20]">sourced &amp; door-delivered.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#344054] md:text-xl">
                From Seoul distributors to your door — <strong>India, UK, UAE
                (Dubai), Hong Kong, USA, Canada</strong>. Duty paid on our end.
                No CDSCO, no CPNP, no FDA, no ESMA, no Health Canada licence
                needed from you.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <a
                  href={CONTACT.whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="kbeauty-whatsapp-hero"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-[#25D366] px-6 py-4 font-semibold text-[#0B111A] shadow-hard hover:bg-[#1FBE5A]"
                >
                  <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
                  Get a K-beauty quote
                </a>
                <a
                  href={CONTACT.telHref}
                  data-testid="kbeauty-call-hero"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#101828] bg-white px-6 py-4 font-semibold text-[#101828] hover:bg-[#101828] hover:text-white"
                >
                  <Phone className="h-5 w-5" strokeWidth={2.5} />
                  {CONTACT.phoneDisplay}
                </a>
              </div>

              <div
                data-testid="kbeauty-no-license"
                className="mt-6 flex items-start gap-3 border-2 border-[#101828] bg-[#25D366]/10 p-4"
              >
                <Check className="mt-0.5 h-5 w-5 flex-none text-[#25D366]" strokeWidth={3} />
                <p className="text-sm leading-relaxed text-[#101828]">
                  <strong>No import licence needed from you.</strong> We import
                  Korean cosmetics on our own registrations (CDSCO India,
                  CPNP-UK Responsible Person, ESMA Dubai, FDA MoCRA USA, Health
                  Canada CNF) and hand-deliver to your address as a private
                  consignee. Individuals, dropshippers, resellers, salon owners
                  — everyone qualifies.
                </p>
              </div>

              <div
                id="kbeauty-tldr"
                data-testid="kbeauty-tldr"
                className="mt-6 border-2 border-[#101828] bg-[#FDB022] p-5 md:p-6"
              >
                <p className="font-mono-accent text-[#101828]">TL;DR — DIRECT ANSWER</p>
                <p className="mt-2 text-base leading-relaxed text-[#101828]">
                  Want authentic Korean cosmetics — COSRX, Innisfree, Laneige,
                  Sulwhasoo, Beauty of Joseon, Anua, Numbuzin, Torriden, and
                  hundreds more — sourced from authorised Seoul distributors
                  and delivered <strong>duty-paid to your door</strong> in
                  India, UK, UAE (Dubai), Hong Kong, USA, or Canada — without
                  acquiring any cosmetics-import licence yourself? Custom
                  Clearance Partner does exactly that. WhatsApp{" "}
                  <a href={CONTACT.whatsappHref} className="font-bold underline">
                    +91 78003 20078
                  </a>{" "}
                  with your brand list and destination.
                </p>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="overflow-hidden border-2 border-[#101828]">
                <img
                  src={heroImg}
                  alt="Air cargo — K-beauty sourcing and DDP delivery"
                  className="h-72 w-full object-cover md:h-96"
                  loading="eager"
                />
              </div>
              <div className="mt-4 grid grid-cols-3 gap-3">
                <div className="border-2 border-[#101828] bg-white p-3">
                  <div className="font-display text-xl font-black leading-none text-[#101828]">6</div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#475467]">DESTINATIONS</div>
                </div>
                <div className="border-2 border-[#101828] bg-[#101828] p-3">
                  <div className="font-display text-xl font-black leading-none text-[#FDB022]">DDP</div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#98A2B3]">DUTY PAID</div>
                </div>
                <div className="border-2 border-[#101828] bg-[#FDB022] p-3">
                  <div className="font-display text-xl font-black leading-none text-[#101828]">2–9d</div>
                  <div className="font-mono-accent mt-1 text-[10px] text-[#101828]">DOOR-TO-DOOR</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Destinations */}
      <section className="border-b border-[#E4E7EC] bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <p className="overline mb-4">§ Destinations we DDP</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
            Six countries, one workflow.
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {DESTINATIONS.map((d, i) => (
              <motion.article
                key={d.code}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className="border-2 border-[#101828] bg-[#F9FAFB] p-6"
                data-testid={`kbeauty-dest-${d.code}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl leading-none">{d.flag}</span>
                  <div>
                    <div className="font-display text-lg font-bold text-[#101828]">{d.name}</div>
                    <div className="font-mono-accent text-[#D92D20]">{d.code}</div>
                  </div>
                </div>
                <div className="font-mono-accent mt-4 text-[10px] text-[#475467]">
                  {d.ports}
                </div>
                <p className="mt-3 text-sm leading-relaxed text-[#475467]">{d.detail}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="border-b border-[#E4E7EC] bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <p className="overline mb-4">§ Categories we source</p>
              <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
                Every K-beauty category, sourced authentic.
              </h2>
              <p className="mt-6 text-sm leading-relaxed text-[#475467]">
                We source directly from authorised distributors in Seoul,
                Busan, and Incheon. No parallel imports, no counterfeits — every
                consignment ships with brand invoice, batch/lot codes, and
                manufacturing dates.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-[#101828]">
                <strong>Brands we regularly source:</strong> COSRX, Innisfree,
                Laneige, Sulwhasoo, Beauty of Joseon, Anua, Numbuzin, Torriden,
                Some By Mi, Mediheal, Etude House, Missha, TIRTIR, Skin1004,
                Round Lab, Isntree, Purito, Pyunkang Yul, Klairs, and dozens
                more.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="grid grid-cols-1 gap-0 border-2 border-[#101828] md:grid-cols-2">
                {PRODUCTS.map((p, i) => (
                  <li
                    key={p}
                    className={`bg-white p-4 ${
                      i % 2 === 0 ? "md:border-r md:border-[#101828]" : ""
                    } ${i < PRODUCTS.length - 2 ? "border-b border-[#101828]" : ""}`}
                    data-testid={`kbeauty-product-${i}`}
                  >
                    <div className="flex items-start gap-2 text-sm text-[#101828]">
                      <Check className="mt-0.5 h-4 w-4 flex-none text-[#D92D20]" strokeWidth={3} />
                      <span>{p}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-[#E4E7EC] bg-white">
        <div className="mx-auto max-w-4xl px-5 py-20 md:px-8 md:py-28">
          <p className="overline mb-4">§ K-Beauty sourcing FAQ</p>
          <h2 className="font-display text-3xl font-black leading-tight tracking-tight text-[#101828] sm:text-4xl md:text-5xl">
            Straight answers.
          </h2>
          <div className="mt-10 border-2 border-[#101828] bg-[#F9FAFB]">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`kb-fq-${i}`}
                  className="border-b border-[#E4E7EC] last:border-b-0"
                  data-testid={`kbeauty-faq-${i}`}
                >
                  <AccordionTrigger className="px-5 py-4 text-left font-display text-base font-bold tracking-tight text-[#101828] hover:no-underline md:text-lg">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-5 pb-5 text-sm leading-relaxed text-[#475467]">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="mt-8">
            <Link
              to="/express-clearance-partner-india"
              data-testid="kbeauty-to-express"
              className="inline-flex items-center gap-2 font-mono-accent text-[#101828] hover:text-[#D92D20]"
            >
              ARE YOU AN EXPRESS COMPANY? SEE OUR PARTNER PROGRAM
              <ArrowRight className="h-3 w-3" strokeWidth={3} />
            </Link>
          </div>
        </div>
      </section>

      <section className="border-b border-[#E4E7EC] bg-[#D92D20] text-white">
        <div className="mx-auto max-w-4xl px-5 py-16 text-center md:px-8">
          <p className="font-mono-accent text-white/80">READY TO ORDER K-BEAUTY?</p>
          <h2 className="mt-4 font-display text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl md:text-5xl">
            Send us the brand list, destination, and volume. We reply in 15 minutes.
          </h2>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="kbeauty-whatsapp-bottom"
              className="inline-flex items-center justify-center gap-2 border-2 border-white bg-[#25D366] px-6 py-4 font-semibold text-[#0B111A] hover:bg-[#1FBE5A]"
            >
              <MessageCircle className="h-5 w-5" strokeWidth={2.5} />
              WhatsApp +91 78003 20078
            </a>
            <a
              href={CONTACT.telHref}
              data-testid="kbeauty-call-bottom"
              className="inline-flex items-center justify-center gap-2 border-2 border-white bg-transparent px-6 py-4 font-semibold text-white hover:bg-white hover:text-[#D92D20]"
            >
              <Phone className="h-5 w-5" strokeWidth={2.5} />
              Call the desk
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingCTA />
    </div>
  );
}
